import { useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { AuthContext } from "../../context/AuthContext";
import { CloudContext } from "../../context/CloudContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import Flag from 'react-flagkit';
import { useSnackbar } from 'notistack';

import config from '../../config.js';

import { PLAN, getPlan, isUnlimited, isAccessExpired, daysRemaining } from '../../utils/accessControl';

import {getPresignedDocumentURLS3} from "../../utils/interfaceS3";

import AboutDialog from "../../dialog/AboutDialog"

export const AccountPopover = (props) => {
  const { anchorEl, onClose, open, language, setLanguage} = props;

  const {  i18n, t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { user, signOut, idToken, jwt } = useContext(AuthContext);
   const { region, idpool } = useContext(CloudContext);

  const [openDialogAbout, setOpenDialogAbout] = useState(false);
  const [accessInfo, setAccessInfo] = useState(null);

  useEffect(() => {
    if (!open || !jwt) return;
    let active = true;
    axios
      .get(`${config.apiUrl}/api/users/me`, {
        headers: { Authorization: `Bearer ${jwt}` },
      })
      .then((res) => { if (active) setAccessInfo(res.data); })
      .catch(() => { if (active) setAccessInfo(null); });
    return () => { active = false; };
  }, [open, jwt]);

  const plan = accessInfo ? getPlan(accessInfo) : null;
  const planLabel =
    plan === PLAN.PAID ? t('PlanPaid')
    : plan === PLAN.TRIAL ? t('PlanTrial')
    : t('PlanUnlimited');
  const unlimited = accessInfo ? isUnlimited(accessInfo) : true;
  const expired = accessInfo ? isAccessExpired(accessInfo) : false;
  const remaining = accessInfo ? daysRemaining(accessInfo) : null; // whole days; null when unlimited

  let navigate = useNavigate();

  const handleChangeLanguage = (event) => {
    setLanguage(event.target.value);
    i18n.changeLanguage(event.target.value);
    //let's store it for the next login
    localStorage.setItem('languageMirukuru', event.target.value);
  };

  const handleSignOut = (e) => {
      
    signOut().then(res => {
        let path = `/login`; 
        navigate(path);
      })
    };

    const handleAbout = (e) => {
      setOpenDialogAbout(true);
    };  

    const downloadManualFile = async (manualKey) => {
      const url = await getPresignedDocumentURLS3(idToken, region, idpool, manualKey, 3600);
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Failed to fetch manual");
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      const filename = manualKey.split("/").pop() || "user-manual.pdf";

      downloadLink.href = objectUrl;
      downloadLink.download = filename;
      document.body.appendChild(downloadLink);

      try {
        downloadLink.click();
      } finally {
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(objectUrl);
      }
    };

    const handleDownloadManual = async () => {
      const manualKey = language === 'jp' ? config.userManualJPN : config.userManualENG;

      if (!manualKey) {
        enqueueSnackbar(`${t("errorDownloadFile")}`, {variant:'error'} );
        return;
      }

      try {
        await downloadManualFile(manualKey);
      } catch (error) {
        console.log(error);
        enqueueSnackbar(`${t("errorDownloadFile")}`, {variant:'error'} );
      }
    };

  return (
    <>
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom'
      }}
      onClose={onClose}
      open={open}
      slotProps={{ paper: { sx: { mt: 1, width: 200 } } }}
    >
      <Box
        sx={{
          py: 1.5,
          px: 3
        }}
      >
        <Typography variant="overline">
          {t("Account")}
        </Typography>
        {user && 
        <>
        <Typography
          color="text.secondary"
          variant="body2"
        >
           {user.name}
        </Typography>
      
      {user["custom:company"] && 

        <Box sx={{pt: 1}}>
        <Typography variant="overline">
         {t("Company")}
        </Typography>
        {user && 
        <>
        <Typography
          color="text.secondary"
          variant="body2"
        >
           {user["custom:company"]}
        </Typography></>}
      </Box>}
      {accessInfo && (
        <Box sx={{ pt: 1 }}>
          <Typography variant="overline">
            {t("Plan")}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {planLabel}
          </Typography>
          {!unlimited && (
            <>
              <Typography color={expired ? 'error' : 'text.secondary'} variant="body2">
                {expired
                  ? t("AccessExpiredLabel")
                  : `${t("AccessValidUntil")} ${new Date(accessInfo.accessExpiresAt).toLocaleDateString()}`}
              </Typography>
              {!expired && remaining !== null && (
                <Typography color="text.secondary" variant="caption">
                  {t("DaysRemainingValue", { count: remaining })}
                </Typography>
              )}
            </>
          )}
        </Box>
      )}</>}
      </Box>
      <Divider />
      <Box sx={{paddingX:3, paddingY:1}}>
      <FormControl>
      <RadioGroup
        row
        aria-labelledby="demo-form-control-label-placement"
        name="position"
        value={language}
        defaultValue={"jp"}
        onChange={handleChangeLanguage}
      >
        <FormControlLabel
          value="jp"
          control={<Radio />}
          label={<Box sx={{paddingTop:1}}><Flag country="JP" /></Box>}
          labelPlacement="end"
        />
        <FormControlLabel
          value="en"
          control={<Radio />}
          label={<Box sx={{paddingTop:1}}><Flag country="GB" /></Box>}
          labelPlacement="end"
        />
      </RadioGroup>  
      </FormControl>
      </Box>
      <Divider />
      <MenuList
        disablePadding
        dense
        sx={{
          p: '8px',
          '& > *': {
            borderRadius: 1
          }
        }}
      >
        <MenuItem onClick={handleDownloadManual}>
         <ListItemIcon>
              <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("DownloadUserManual")}</ListItemText>
        </MenuItem>
      </MenuList>
      <Divider />
      <MenuList
        disablePadding
        dense
        sx={{
          p: '8px',
          '& > *': {
            borderRadius: 1
          }
        }}
      >
        <MenuItem onClick={handleAbout}>
         <ListItemIcon>
              <InfoOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("AboutButton")}</ListItemText>
        </MenuItem>
      </MenuList>
      <Divider />
      <MenuList
        disablePadding
        dense
        sx={{
          p: '8px',
          '& > *': {
            borderRadius: 1
          }
        }}
      >
        <MenuItem onClick={handleSignOut}>
          <ListItemIcon>
              <ExitToAppIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("Signout")}</ListItemText>
        </MenuItem>
      </MenuList>
    </Popover>
    <AboutDialog open={openDialogAbout} setOpen={setOpenDialogAbout}/>
    </>
  );
};

AccountPopover.propTypes = {
  anchorEl: PropTypes.any,
  onClose: PropTypes.func,
  open: PropTypes.bool.isRequired
};
